import { findCountry, resolveSubdivisionName } from './geoReference';

const ROLE_FIELDS = [
  'line1',
  'line2',
  'city',
  'town',
  'county',
  'state',
  'postCode',
  'country',
  'formattedAddress'
];

export function indexAddressComponents(place) {
  const raw = place?.addressComponents || place?.address_components || [];
  const byType = new Map();
  for (const component of raw) {
    const long = component?.longText ?? component?.long_name ?? '';
    const short = component?.shortText ?? component?.short_name ?? '';
    for (const type of component?.types || []) {
      if (!byType.has(type)) byType.set(type, { long, short });
    }
  }
  return byType;
}

const longOf = (byType, type) => byType.get(type)?.long || '';
const shortOf = (byType, type) => byType.get(type)?.short || '';
const formattedCountryName = (country) => country?.name || '';

function firstOf(byType, types) {
  for (const type of types) {
    const value = longOf(byType, type);
    if (value) return value;
  }
  return '';
}

const DEFAULT_SUBDIVISION_TYPES = ['administrative_area_level_1'];

const COUNTRY_ADDRESS_FORMATS = {
  GB: {
    town: ['locality', 'sublocality_level_1'],
    city: ['postal_town', 'locality'],
    county: ['administrative_area_level_2']
  },
  IE: {
    town: ['sublocality_level_1'],
    city: ['locality', 'postal_town'],
    county: []
  },
  US: {
    town: [],
    city: ['locality', 'sublocality_level_1', 'neighborhood'],
    county: ['administrative_area_level_2']
  },
  CA: {
    town: [],
    city: ['locality', 'sublocality_level_1'],
    county: ['administrative_area_level_2']
  },
  AU: {
    town: [],
    city: ['locality'],
    county: []
  },
  NZ: {
    town: ['sublocality_level_1'],
    city: ['locality'],
    county: []
  }
};

export function addressFormatFor(countryCode) {
  return (
    COUNTRY_ADDRESS_FORMATS[String(countryCode || '').toUpperCase()] || null
  );
}

function applyFormat(byType, format) {
  const city = firstOf(byType, format.city || []);
  const town = firstOf(byType, format.town || []);
  const county = firstOf(byType, format.county || []);
  return {
    town: town && town !== city ? town : '',
    city,
    county: county && county !== city ? county : ''
  };
}

function genericLocality(byType) {
  const settlement = firstOf(byType, [
    'locality',
    'postal_town',
    'sublocality_level_1',
    'sublocality'
  ]);
  const district = longOf(byType, 'administrative_area_level_3');
  const division = longOf(byType, 'administrative_area_level_2');

  if (settlement && district)
    return { town: settlement, city: district, county: division };
  if (settlement) return { town: '', city: settlement, county: division };
  if (district) return { town: '', city: district, county: division };
  if (division) return { town: '', city: division, county: '' };
  return { town: '', city: '', county: '' };
}

const SUBDIVISION_PREFIX =
  /^(?:county|co\.?|province|region|state|prefecture)\s+/i;
const SUBDIVISION_SUFFIX =
  /\s+(?:county|province|region|state|prefecture|oblast|governorate)$/i;

export function subdivisionCandidates(
  byType,
  types = DEFAULT_SUBDIVISION_TYPES
) {
  const candidates = [];
  const push = (value) => {
    if (value && !candidates.includes(value)) candidates.push(value);
  };

  for (const type of types) {
    const entry = byType.get(type);
    if (!entry) continue;
    for (const value of [entry.long, entry.short]) {
      if (!value) continue;
      push(value);
      push(
        String(value)
          .replace(SUBDIVISION_PREFIX, '')
          .replace(SUBDIVISION_SUFFIX, '')
          .trim()
      );
    }
  }
  return candidates;
}

function buildLine1(byType, formattedAddress, { hasSubpremise } = {}) {
  const postBox = longOf(byType, 'post_box');
  const streetNumber = longOf(byType, 'street_number');
  const route = longOf(byType, 'route');
  const premise = longOf(byType, 'premise');
  const anchor = route || premise;

  if (!anchor && !streetNumber && !postBox) return '';

  if (!hasSubpremise && anchor) {
    const firstSegment = String(formattedAddress || '')
      .split(',')[0]
      .trim();
    if (firstSegment && firstSegment.includes(anchor)) return firstSegment;
  }

  return [postBox, streetNumber, anchor].filter(Boolean).join(' ').trim();
}

export function mapPlaceToAddress(place, { reference, fallbackLine1 } = {}) {
  const byType = indexAddressComponents(place);
  const formattedAddress =
    place?.formattedAddress || place?.formatted_address || '';
  const googleCountryCode = shortOf(byType, 'country').toUpperCase();
  const referenceCountry = findCountry(reference, googleCountryCode);
  const countryCode = referenceCountry ? referenceCountry.code : '';

  const line2 = longOf(byType, 'subpremise');
  let line1 = buildLine1(byType, formattedAddress, {
    hasSubpremise: Boolean(line2)
  });
  const format = addressFormatFor(googleCountryCode);
  const { town, city, county } = format
    ? applyFormat(byType, format)
    : genericLocality(byType);

  const state = resolveSubdivisionName(
    reference,
    countryCode,
    subdivisionCandidates(byType, format?.subdivision)
  );

  if (!line1 && fallbackLine1) {
    const candidate = String(fallbackLine1).trim();
    const duplicates = [
      town,
      city,
      county,
      state,
      formattedCountryName(referenceCountry)
    ]
      .filter(Boolean)
      .some((value) => value.toLowerCase() === candidate.toLowerCase());
    if (candidate && !duplicates) line1 = candidate;
  }

  return {
    line1,
    line2,
    town,
    city,
    county,
    state,
    postCode: longOf(byType, 'postal_code'),
    country: countryCode,
    countryCode,
    // What Google itself said, before the reference list had a chance to drop
    // it. The country guard has to compare on this: a reference miss would
    // otherwise read as "no country" and let a foreign result through.
    googleCountryCode,
    formattedAddress
  };
}

export function buildAddressPatch(
  mapped,
  fieldNames = {},
  { includeCountry = false } = {}
) {
  const patch = {};
  for (const role of ROLE_FIELDS) {
    if (role === 'country' && !includeCountry) continue;
    const field = fieldNames[role];
    if (!field) continue;
    const value = mapped?.[role];
    if (value === undefined || value === null || value === '') continue;
    patch[field] = value;
  }
  return patch;
}

export function staleAutofillPatch(filled, current = {}) {
  const patch = {};
  if (!filled) return patch;
  for (const [field, value] of Object.entries(filled)) {
    if (!value) continue;
    if (current?.[field] === value) patch[field] = '';
  }
  return patch;
}

export function toSuggestionOption(suggestion) {
  const placeId = suggestion?.placeId;
  if (!placeId) return null;
  const text = suggestion.text || '';
  const primary = suggestion.mainText || text;
  const secondary = suggestion.secondaryText || '';
  return {
    id: placeId,
    placeId,
    label: text || [primary, secondary].filter(Boolean).join(', '),
    primary,
    secondary
  };
}
